/* public-i18n-migrated */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useI18n } from "@/i18n";
import { PublicLanguageSelect } from '@/components/PublicLanguageSelect';
import { pickLang } from "@/lib/toolbox";
import AllRounderLogo from "@/components/AllRounderLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DynamicPage() {
  const { slug } = useParams();
  const { lang , tx} = useI18n();
  const [state, setState] = useState("loading"); // loading | ok | notfound
  const [page, setPage] = useState(null);

  useEffect(() => {
    setState("loading");
    axios
      .get(`${API}/pages/${slug}`)
      .then((r) => {
        setPage(r.data);
        setState("ok");
      })
      .catch(() => setState("notfound"));
  }, [slug]);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#0A369D]" />
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center text-center px-6">
        <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#94A3B8]">404</p>
        <h1 className="font-display mt-3 text-3xl font-black text-[#0F172A]">{tx("페이지를 찾을 수 없습니다")}</h1>
        <p className="mt-2 text-[#475569]">{tx('No page exists at /{slug}.',{slug})}</p>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#0A369D] text-white font-semibold">
          <ArrowLeft className="w-4 h-4" /> {tx("홈으로")} </Link>
      </div>
    );
  }

  const title = pickLang(page.title, lang);
  const subtitle = pickLang(page.subtitle, lang);
  const blocks = page.blocks || [];

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]" data-testid={`dynamic-page-${page.slug}`}>
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/70">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="dynamic-page-home"><AllRounderLogo variant="horizontal" /></Link>
          <PublicLanguageSelect id="dynamic-page-language"/>
          <Link to="/" className="text-[12px] uppercase tracking-[0.16em] font-bold text-[#475569] hover:text-[#0A369D] flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> {tx("Home")} </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        {page.hero_image ? (
          <div className="relative h-[42vh] min-h-[280px] w-full">
            <img src={page.hero_image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A369D]/70 via-[#0A369D]/25 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-8">
                <motion.h1
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                  className="font-display text-4xl sm:text-6xl font-black tracking-tight text-white leading-[1.05]"
                >
                  {title}
                </motion.h1>
                {subtitle ? <p className="mt-3 text-white/85 text-lg max-w-2xl">{subtitle}</p> : null}
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-16 pb-8">
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="font-display text-4xl sm:text-6xl font-black tracking-tight text-[#0F172A] leading-[1.05]"
            >
              {title}
            </motion.h1>
            {subtitle ? <p className="mt-3 text-[#475569] text-lg max-w-2xl">{subtitle}</p> : null}
          </div>
        )}
      </section>

      {/* Blocks */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-14">
        {blocks.map((b, i) => {
          const heading = pickLang(b.heading, lang);
          const text = pickLang(b.text, lang);
          const hasImg = !!b.image;
          return (
            <motion.section
              key={b.id || i}
              data-testid={`page-block-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5 }}
              className={`grid grid-cols-1 ${hasImg ? "lg:grid-cols-2" : ""} gap-8 items-center`}
            >
              {hasImg ? (
                <div className={`rounded-2xl overflow-hidden border border-slate-100 shadow-[0_10px_30px_-15px_rgba(10,54,157,0.25)] ${i % 2 ? "lg:order-2" : ""}`}>
                  <img src={b.image} alt={heading} className="w-full h-full object-cover" />
                </div>
              ) : null}
              <div>
                {heading ? (
                  <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-[#0F172A]">{heading}</h2>
                ) : null}
                {text ? (
                  <p className="mt-4 text-[#475569] leading-relaxed whitespace-pre-line text-[15px] sm:text-base">{text}</p>
                ) : null}
              </div>
            </motion.section>
          );
        })}
      </div>

      <footer className="bg-[#0A369D] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 flex items-center justify-between">
          <AllRounderLogo variant="horizontal" color="#FFFFFF" />
          <Link to="/" className="text-[12px] uppercase tracking-[0.16em] font-bold text-white/80 hover:text-white">{tx("Home")}</Link>
        </div>
      </footer>
    </div>
  );
}
